import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styleable from 'react-styleable';
import { injectIntl, intlShape } from 'react-intl';
import includes from 'lodash/includes';
import { Plus, Upload } from '../../../helpers/icon-dictionary';
import Button from '../../button/index';
import { DARK } from '../../button/variants';
import messages from '../messages';
import css from '../index.scss';
import DataGrid from '../../../components/data-grid/index';
import { RollOverRowRenderer } from '../../../components/renderers/index';
import EmptyState from '../../empty-state/index';
import { createRolloverRows } from '../../../helpers/grid-util';

function getGridColumns(intl) {
    return [
        {
            key: 'studentFirstName',
            name: `${intl.formatMessage(messages.studentFirstName)}`
        },
        {
            key: 'studentLastName',
            name: `${intl.formatMessage(messages.studentSurname)}`
        },
        {
            key: 'year',
            name: `${intl.formatMessage(messages.year)}`,
            width: 60
        },
        {
            key: 'className',
            name: `${intl.formatMessage(messages.className)}`
        },
        {
            key: 'title',
            name: `${intl.formatMessage(messages.title)}`,
            width: 60
        },
        {
            key: 'teacherFirstName',
            name: `${intl.formatMessage(messages.teacherFirstName)}`
        },
        {
            key: 'teacherLastName',
            name: `${intl.formatMessage(messages.teacherSurname)}`
        },
        {
            key: 'teacherEmail',
            name: `${intl.formatMessage(messages.teacherEmail)}`
        },
        {
            key: 'previousUsername',
            name: `${intl.formatMessage(messages.previousUsername)}`
        }
    ];
}

class RollOverUpload extends Component {
    constructor(props) {
        super(props);

        this.maxFileSize = 1024 * 1024; // 1MB
        this.fileReader = new FileReader();

        this.handleOnUpload = this.handleOnUpload.bind(this);
        this.handleOnFileSelection = this.handleOnFileSelection.bind(this);
    }

    handleOnUpload() {
        this.uploadFile.click();
    }

    handleOnFileSelection = (event) => {
        const {
            intl,
            selectedProduct
        } = this.props;

        const file = event.target.files[0];
        const fileReader = new FileReader();
        const isServerError = false;
        const showModal = true;
        const uploadingRollover = false;

        fileReader.onload = (e) => {
            const fileContent = e.target.result.split(',')[1];

            if (this.validateFileType(file.type)) {
                const fileSize = file.size / this.maxFileSize;
                if (fileSize > 1) {
                    const errorMessage = intl.formatMessage(messages.fileSizeMessage);
                    this.props.rolloverUploadFailure({ isServerError, errorMessage, showModal, uploadingRollover, fileSize });
                } else {
                    this.props.rollOverVerifyContentRequest(selectedProduct.id, fileContent);
                }
            } else {
                const isInvalidFile = true;
                const errorMessage = intl.formatMessage(messages.fileFormatValidationMessage);
                this.props.rolloverUploadFailure({ isServerError, errorMessage, showModal, uploadingRollover, isInvalidFile });
            }
        };

        fileReader.onError = () => {
            const messageDetail = intl.formatMessage(messages.pleaseTryUploadingAgain);
            const errorMessage = intl.formatMessage(messages.somethingWentWrong);
            this.props.rolloverUploadFailure({ isServerError, errorMessage, showModal, uploadingRollover, messageDetail });
        };

        fileReader.readAsDataURL(file);
        this.uploadFile.value = '';
    }

    validateFileType = (fileType) => {
        const allowedFileTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        return includes(allowedFileTypes, fileType);
    }

    handleOnBackButton = () => {
        this.props.clearRollOverVerifyData();
        this.props.showSelectionPage();
    }

    render() {
        const {
            intl,
            isVerifyingContent,
            isVerificationSuccessful,
            rolloverItems,
            showDataGrid,
            openRollOverConfirmation
        } = this.props;

        const allowedFileExt = ['.xls', '.xlsx'].join(',');
        const uploadContentCss = [css.content, css.uploadContainer].join(' ');

        this.uploadFile = (element) => {
            this.uploadFile = element;
        };

        const uploadButton = (<Button
            containerClassName={css.uploadFile}
            handleOnClick={this.handleOnUpload}
            icon={Plus}
            iconClassName={isVerifyingContent ? css.svgContainer : css.svgIconContainer}
            label={intl.formatMessage(messages.uploadFile)}
            loading={isVerifyingContent}
            loadingLabel={intl.formatMessage(messages.verifying)} />);

        this.rows = createRolloverRows(rolloverItems);
        const rowGetter = rowNumber => this.rows[rowNumber];

        const component = showDataGrid ? (
            <div className={css.dataGridWrapper}>
                <div className={css.uploadContentWrapper}>
                    <div className={css.uploadContentLabel}>{intl.formatMessage(messages.uploadMessage)}</div>
                    <div className={css.uploadButtonContainer}>
                        {uploadButton}
                    </div>
                </div>
                <div className={css.dataGridContainer}>
                    <DataGrid
                        columns={getGridColumns(intl)}
                        rowGetter={rowGetter}
                        rowKey="index"
                        rowRenderer={RollOverRowRenderer}
                        rowsCount={this.rows.length}
                        rowSelectionEnabled={false}
                        selectedRows={[]}
                        showSelectionColumn={false}
                    />
                </div>
            </div>) :
            (
                <div className={css.emptyOuterContainer}>
                    <EmptyState
                        iconName={Upload}
                        message={intl.formatMessage(messages.uploadMessage)} />
                    {uploadButton}
                </div>
            );

        return (
            <div className={css.rollOverComponent}>
                <div className={css.header}>
                    <div className={css.title}>{intl.formatMessage(messages.header)}</div>
                </div>
                <div className={uploadContentCss}>
                    <div className={css.contentWrapper}>
                        <input
                            accept={allowedFileExt}
                            className={css.browseButton}
                            id="file"
                            name="file"
                            onChange={event => this.handleOnFileSelection(event)}
                            ref={this.uploadFile}
                            type="file" />
                        {component}
                    </div>
                </div>
                <div className={css.rollOverFooter}>
                    <Button
                        disabled={!isVerificationSuccessful}
                        handleOnClick={openRollOverConfirmation}
                        label={intl.formatMessage(messages.confirm)}
                        variant={DARK} />
                    <Button
                        disabled={isVerifyingContent}
                        handleOnClick={this.handleOnBackButton}
                        label={intl.formatMessage(messages.back)}
                        variant={DARK} />
                </div>
            </div>
        );
    }
}

RollOverUpload.propTypes = {
    intl: intlShape.isRequired,
    showSelectionPage: PropTypes.func,
    rolloverUploadFailure: PropTypes.func,
    rollOverVerifyContentRequest: PropTypes.func,
    selectedProduct: PropTypes.object,
    isVerifyingContent: PropTypes.bool,
    isVerificationSuccessful: PropTypes.bool,
    rolloverItems: PropTypes.array,
    showDataGrid: PropTypes.bool,
    clearRollOverVerifyData: PropTypes.func,
    openRollOverConfirmation: PropTypes.func
};

export default injectIntl(styleable(css)(RollOverUpload));



// WEBPACK FOOTER //
// ./app/components/roll-over/pages/upload.js